import { css } from '@emotion/react';
import { Link, PageProps, withPrefix } from 'gatsby';
import { ContactSection } from '@/components/general/ContactSection';
import { Layout } from '@/components/general/Layout';
import { NewsCard } from '@/components/general/NewsCard';
import { PageHeader } from '@/components/general/PageHeader';
import { PaginationNav } from '@/components/general/PaginationNav';
import { Seo } from '@/components/general/Seo';
import { pagesPath } from '@/lib/pagesPath';
import { getScreen } from '@/styles/media-query';
import { NewsCategory, NewsForList } from '@/types/cms-types';

type TemplateProps = {
  pageContext: {
    nodes: NewsForList[];
    category?: NewsCategory;
    categories: NewsCategory[];
    totalCount: number;
    perPage: number;
    currentPage: number;
  };
} & PageProps;

const Template: React.FC<TemplateProps> = ({
  pageContext: {
    nodes,
    category: currentCategory,
    categories,
    totalCount,
    perPage,
    currentPage,
  },
  location,
}) => {
  const pageProvider = currentCategory
    ? pagesPath.news._category(currentCategory.slug)
    : pagesPath.news;

  return (
    <Layout location={location}>
      <Seo
        title={
          currentCategory
            ? `お知らせ・${currentCategory.title}`
            : 'お知らせ一覧'
        }
        description={
          currentCategory?.slug === 'service'
            ? '【Uniforce】IPO準備クラウドのお知らせ。サービスについてのアップデートや、メンテナンスなどの情報を記載しています。'
            : currentCategory?.slug === 'event'
            ? '【Uniforce】IPO準備クラウドのお知らせ。イベントやセミナーの案内などの情報を記載しています。'
            : currentCategory?.slug === 'media'
            ? '【Uniforce】IPO準備クラウドのお知らせ。メディア掲載などの情報を記載しています。'
            : '【Uniforce】IPO準備クラウドのお知らせ。最新記事やセミナーの案内などの情報を記載しています。'
        }
      />
      <div
        css={css`
          text-align: center;
          padding-bottom: 64px;

          @media ${getScreen('pc')} {
            padding-bottom: 80px;
          }
        `}
      >
        <PageHeader en="INFORMATION" title="お知らせ">
          <div
            css={css`
              pointer-events: none;
              position: absolute;
              right: calc(50% + 315px);
              bottom: -33px;

              @media ${getScreen('pc', '未満')} {
                width: 82px;
                right: calc(50% + 85px);
                bottom: 4px;
              }
            `}
          >
            <img
              css={css`
                max-width: 100%;
                height: auto;
              `}
              src={withPrefix('/images/news/header-decoration.png')}
              width="185"
              height="149"
              alt=""
            />
          </div>
        </PageHeader>

        <div
          css={css`
            @media ${getScreen('pc')} {
              padding: 0 30px;
            }
          `}
        >
          {/* カテゴリー一覧 */}
          <nav
            css={css`
              border-bottom: 1px solid #ebeff1;
              max-width: 1040px;
              margin: 43px auto 0;

              @media ${getScreen('pc')} {
                margin-top: 90px;
              }
            `}
          >
            <ul
              css={css`
                padding: 0 20px;
                display: flex;
                width: max-content;
                max-width: 100%;
                margin: 0 auto;
                overflow-x: auto;
                gap: 20px;
                scrollbar-width: none;
                -ms-overflow-style: none;

                @media ${getScreen('pc')} {
                  gap: 40px;
                }

                &::-webkit-scrollbar {
                  display: none;
                }
              `}
            >
              {[
                {
                  slug: 'ALL',
                  title: 'ALL',
                  to: pagesPath.news.$url(),
                  isCurrent: !currentCategory,
                },
                ...categories.map((category) => ({
                  slug: category.slug,
                  title: category.title,
                  to: pagesPath.news._category(category.slug).$url(),
                  isCurrent:
                    currentCategory && currentCategory.slug === category.slug,
                })),
              ].map((item) => (
                <li key={item.slug}>
                  <Link
                    css={css`
                      position: relative;
                      display: inline-block;
                      font-size: 1.6rem;
                      color: #5a5b6a;
                      width: 105px;
                      min-width: max-content;
                      padding: 0 10px 15px;
                      white-space: nowrap;

                      @media ${getScreen('pc')} {
                        width: 153px;
                      }

                      &:hover:after,
                      &[aria-current='page']:after {
                        content: '';
                        display: block;
                        width: 100%;
                        height: 8px;
                        background-color: #00b3a7;
                        position: absolute;
                        bottom: 0;
                        left: 0;
                        border-radius: 16px 16px 0 0;
                      }

                      &:hover,
                      &[aria-current='page'] {
                        color: #00b3a7;
                      }
                    `}
                    to={item.to}
                    aria-current={item.isCurrent ? 'page' : undefined}
                  >
                    {item.title}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
          <section>
            <ul
              css={css`
                display: flex;
                flex-direction: column;
                max-width: 1040px;
                gap: 40px;
                justify-content: center;
                margin: 48px auto 0;
                padding: 0 20px;

                @media ${getScreen('pc')} {
                  margin: 80px auto 0;
                  display: grid;
                  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
                }
              `}
            >
              {nodes.map((node) => (
                <li key={node.ipoNewsId}>
                  <NewsCard
                    css={css`
                      height: 100%;
                    `}
                    item={node}
                  />
                </li>
              ))}
            </ul>
            {totalCount > perPage && (
              <PaginationNav
                css={css`
                  margin: 40px 0 24px;

                  @media ${getScreen('pc')} {
                    margin: 72px 0 40px;
                  }
                `}
                totalCount={totalCount}
                perPage={perPage}
                currentPage={currentPage}
                pageProvider={pageProvider}
              />
            )}
          </section>
        </div>
      </div>
      <ContactSection
        title1="Uniforce IPO準備クラウドで"
        title2="「IPOの準備を効率的」"
        title3="に"
        title4="進めませんか？"
        text="IPO準備クラウドの機能詳細や無料デモのご相談はこちら"
      />
    </Layout>
  );
};

export default Template;
